export type SortDirection = 'asc' | 'desc';

/** One sorted column, or `null` for the table's own order. */
export interface SortState<K extends string> {
    key: K;
    direction: SortDirection;
}

/**
 * What a click on a column header asks for — the project overview's cycle, so every list in the app
 * answers a click the same way: a new column starts ascending, the sorted one turns descending, and a
 * third click goes back to the table's own order.
 */
export function nextSort<K extends string>(current: SortState<K> | null, key: K): SortState<K> | null {
    if (current === null || current.key !== key) {
        return { key, direction: 'asc' };
    }
    return current.direction === 'asc' ? { key, direction: 'desc' } : null;
}

/** The direction one column shows, given the table's sort. */
export function directionOf<K extends string>(current: SortState<K> | null, key: K): SortDirection | null {
    return current !== null && current.key === key ? current.direction : null;
}

/** The `aria-sort` value of one column header. */
export function ariaSortOf<K extends string>(
    current: SortState<K> | null,
    key: K,
): 'ascending' | 'descending' | 'none' {
    const direction = directionOf(current, key);
    if (direction === null) {
        return 'none';
    }
    return direction === 'asc' ? 'ascending' : 'descending';
}
